/**
 * Format API error for display
 * @param {Error} error - Error object from API call
 * @returns {string} - Formatted error message
 */
export const formatError = (error) => {
  if (error.response) {
    // Server responded with error status
    const { data, status } = error.response;

    if (data?.detail) {
      // FastAPI validation error or custom detail
      if (typeof data.detail === 'string') {
        return data.detail;
      }
      // Array of validation errors
      if (Array.isArray(data.detail)) {
        return data.detail.map(err => {
          const field = err.loc ? err.loc.join('.') : 'Unknown field';
          return `${field}: ${err.msg}`;
        }).join(', ');
      }
    }

    if (data?.message) return data.message;
    if (data?.error) return data.error;

    // Status-specific messages
    switch (status) {
      case 400:
        return 'Invalid request. Please check your input.';
      case 401:
        return 'Unauthorized. Please log in again.';
      case 403:
        return 'You do not have permission to perform this action.';
      case 404:
        return 'Resource not found.';
      case 409:
        return 'Conflict. Resource already exists.';
      case 422:
        return 'Validation error. Please check your input.';
      case 500:
        return 'Server error. Please try again later.';
      default:
        return `Request failed with status ${status}`;
    }
  }

  if (error.request) {
    // Request made but no response
    return 'No response from server. Please check your connection.';
  }

  // Other errors
  return error.message || 'An unexpected error occurred';
};

/**
 * Check if error is a network error
 * @param {Error} error - Error object
 * @returns {boolean}
 */
export const isNetworkError = (error) => {
  return error.request && !error.response;
};

/**
 * Check if error is an authentication error
 * @param {Error} error - Error object
 * @returns {boolean}
 */
export const isAuthError = (error) => {
  return error.response && (error.response.status === 401 || error.response.status === 403);
};

/**
 * Check if error is a validation error
 * @param {Error} error - Error object
 * @returns {boolean}
 */
export const isValidationError = (error) => {
  return error.response && (error.response.status === 400 || error.response.status === 422);
};

/**
 * Extract field-specific errors from validation error
 * @param {Error} error - Error object from API
 * @returns {Object} - Object mapping field names to error messages
 */
export const extractFieldErrors = (error) => {
  const fieldErrors = {};

  if (error.response?.data?.detail && Array.isArray(error.response.data.detail)) {
    error.response.data.detail.forEach(err => {
      if (err.loc && err.loc.length > 0) {
        // Get the field name (skip 'body' prefix from FastAPI)
        const field = err.loc[err.loc.length - 1];
        fieldErrors[field] = err.msg;
      }
    });
  }

  return fieldErrors;
};
